/**
 * Снимок экрана банка: список операций → операции SKOKA.
 *
 * Человек загружает то, что у него есть, и чаще всего это не чек, а экран
 * банка: «Zolla −17 012 ₽ · Clothing & Shoes · Credit card». Сложить такие
 * строки в одну покупку нельзя, но каждая из них — операция. Экран держится
 * на тех же правилах, что и чек:
 *
 * · **до подтверждения ничего не записано** — список живёт только здесь;
 * · **категория — из справочника человека**, её предлагает модель, человек
 *   меняет тапом; часть плана берётся из категории;
 * · **уже записанное не записывается второй раз**: та же сумма в тот же день
 *   снята с галочки, в том числе когда чек разложен на несколько трат;
 * · **перевод решает человек**: перевод на карту другого банка или другому
 *   человеку бывает и расходом, и переводом между своими счетами — строка
 *   ждёт выбора и сама ничего не записывает;
 * · **поступление записывается так же, как в ручной записи**: категория
 *   дохода, счёт и ожидаемый приход, который оно закрывает. По умолчанию оно
 *   не отмечено: зарплату часто уже записали, когда она пришла, а лишний
 *   приход завышает остаток;
 * · **подпись карты сопоставляется один раз**: «Credit card» — это «Кредитка»,
 *   и в следующий раз приложение это помнит.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { classifyItemPart, formatMoney, kop, sub, ZERO, type Account, type Money, type PeriodIncome, type PlainDate, type PlanPart } from '@skoka/core';
import { categorizeBasket, type StatementOp } from './ai.js';
import { addExpense, addIncomeTx, addTransfer, linkPaymentTx, todayPd, type AppState, type Tx } from './store.js';
import { rememberType } from './op-memory.js';
import { paymentFor } from './payment-match.js';
import { getMeta, setMeta } from './db.js';
import { isNewCategory, modelCatalog, topLevel } from './categories.js';
import { NewCatsAsk, NewCatsBlock, NewModeQuestion, NewTag, useNewCategories } from './new-categories.js';
import { nameKey, rememberedCategories } from './category-memory.js';
import { IncomeLink } from './operation-fields.js';
import { CardAccount, CardCategory, CardPick, CardWhen, NoteField } from './card-fields.js';
import { incomeStreamOptions, partialIncome, pendingRest, restToRecord } from './income-options.js';
import { CategoryGlyph, colorOf } from './category-icon.js';
import { ImportBar, ImportRow } from './import-row.js';
import { SheetWrap } from './sheet.js';
import { dayMonth, plural, pluralWord } from './text.js';
import { Icon } from './icon.js';
import { statementAccountId } from './import-account.js';
import { DateButton } from './date-picker.js';

/** Подпись карты в банке → счёт в SKOKA. Живёт на устройстве. */
const LABELS_KEY = 'bankLabels';
type LabelMap = Record<string, string>;
/** Строка без подписи карты: у всех таких один счёт. */
const NO_LABEL = '';

export { guessAccount } from './import-account.js';

/**
 * Суммы, уже записанные в этот день, — отдельные операции и чеки целиком.
 *
 * Чек теперь раскладывается на траты по категориям, и покупка на 600,94 ₽
 * лежит тремя операциями. Банк покажет её одной строкой, и сравнение только с
 * отдельными операциями записало бы её второй раз.
 */
export function recordedAmounts(txs: readonly Tx[], date: PlainDate, type: Tx['type']): Set<number> {
  const day = txs.filter((t) => t.occurredDate === date && t.type === type);
  const out = new Set<number>(day.map((t) => t.amount));
  const receipts = new Map<string, number>();
  for (const t of day) {
    const key = t.receiptId ?? (t.source === 'receipt_photo' && t.note ? `${t.note}|${t.occurredAt}` : null);
    if (key) receipts.set(key, (receipts.get(key) ?? 0) + t.amount);
  }
  for (const v of receipts.values()) out.add(v);
  return out;
}

/**
 * Как записать строку: расходом, поступлением или переводом. `null` — ещё не
 * решено. Не записывать строку — снять с неё галочку.
 */
type Mode = 'expense' | 'income' | 'transfer';

type Row = {
  op: StatementOp;
  mode: Mode | null;
  /** Записывать ли строку: у решённой строки галочку можно снять. */
  on: boolean;
  categoryId: string;
  /** Категорию подтвердила модель или человек. */
  sure: boolean;
  /** Второй свой счёт перевода: куда ушли деньги или откуда пришли. */
  pairId: string;
  /** Категория поступления. */
  incomeCatId: string;
  /** Какой ожидаемый доход закрывает поступление; пусто — разовые деньги. */
  streamId: string;
  /**
   * Поступление — возврат за покупку: категория тогда расходная (`categoryId`,
   * «за что вернули»), и возврат уменьшает траты в ней.
   */
  refund: boolean;
  /** Ждёт ли человек остаток, если пришло меньше ожидаемого. `null` — не ответил. */
  waitRest: boolean | null;
  /** День и время, поправленные человеком; `null` — как на снимке. */
  date: PlainDate | null;
  time: string | null;
  /** Заметка человека — к названию из банка, а не вместо него. */
  comment: string;
  /** Человек снял «засчитать как оплату платежа». */
  notPayment?: boolean;
};

const spends = (op: StatementOp) => op.direction === 'out' && op.type !== 'transfer';
const earns = (op: StatementOp) => op.direction === 'in' && op.type !== 'transfer';
const dayWord = (d: PlainDate, today: PlainDate) => (d === today ? 'Сегодня' : dayMonth(d, today));
/** «Нашлась 1 операция», «Нашлись 3 операции», «Нашлось 9 операций» — без рода у приложения. */
export const foundText = (n: number): string =>
  `${pluralWord(n, 'Нашлась', 'Нашлись', 'Нашлось')} ${plural(n, 'операция', 'операции', 'операций')}`;

export function StatementReview({ state, operations, dropped, initialAccountId, onDone, onCancel }: {
  state: AppState;
  operations: StatementOp[];
  initialAccountId?: string;
  /** Сколько строк снимка не прочиталось. */
  dropped: number;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const today = todayPd();
  const accounts = state.accounts.filter((a) => !a.archived);
  const [categories, setCategories] = useState(state.categories);
  const catalog = useMemo(() => modelCatalog(categories), [categories]);
  const cats = topLevel(categories);
  const fallbackCat = cats.find((c) => c.id === 'other')?.id ?? cats[0]?.id ?? 'other';
  const incomeCats = categories.filter((c) => c.kind === 'income' && !c.archived);
  const fallbackIncome = incomeCats.find((c) => c.id === 'income.4')?.id ?? incomeCats[0]?.id ?? '';

  /** День для строк без заголовка дня на снимке. */
  const [date, setDate] = useState<PlainDate>(today);
  const dayOf = (r: Row): PlainDate => r.date ?? r.op.date ?? date;
  const undated = operations.some((o) => !o.date);

  const labels = [...new Set(operations.map((o) => o.account ?? NO_LABEL))];
  const [labelMap, setLabelMap] = useState<LabelMap>({});
  useEffect(() => {
    void getMeta<LabelMap>(LABELS_KEY).catch(() => undefined).then((saved) => {
      setLabelMap(Object.fromEntries(labels.map((l) => [
        l, statementAccountId(accounts, l, initialAccountId, saved?.[l]),
      ])));
    });
    // Снимок один на весь экран: подписи не меняются, пока он открыт.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
    Операция с тем же названием уже записывалась — категория та, что дал
    человек. Модель строку с «уверенной» категорией не трогает.
  */
  const remembered = useMemo(() => rememberedCategories(state.transactions, categories).titles,
    // Память берётся на момент открытия снимка.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  const [rows, setRows] = useState<Row[]>(() => operations.map((op) => ({
    op,
    // Покупка записывается расходом, поступление — поступлением, но без
    // галочки. Перевод ждёт решения человека: он бывает и расходом или
    // поступлением, и переводом между своими счетами.
    mode: spends(op) ? 'expense' : earns(op) ? 'income' : null,
    on: spends(op),
    categoryId: remembered.get(nameKey(op.title)) ?? fallbackCat,
    sure: remembered.has(nameKey(op.title)),
    pairId: '',
    incomeCatId: fallbackIncome,
    streamId: '',
    refund: op.refund === true,
    waitRest: null,
    date: null,
    time: null,
    comment: '',
  })));
  const [sorting, setSorting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Строка, открытая в листе. */
  const [open, setOpen] = useState<number | null>(null);
  /** Новые категории для покупок, которым в справочнике нет места. */
  const nc = useNewCategories({
    mode: state.newCategoryMode, categories, onCategories: setCategories,
    ids: rows.map((r) => r.categoryId),
    touched: (n) => rows[n]?.sure ?? true,
    apply: (changes) => setRows((prev) => prev.map((r, n) => {
      const id = changes.get(n);
      return id ? { ...r, categoryId: id, sure: true } : r;
    })),
  });
  /** Категории, которые станут тратами: у расхода и у возврата. */
  const spendCat = (r: Row) => r.mode === 'expense' || (r.mode === 'income' && r.refund);

  useEffect(() => {
    // Категория нужна и переводу: человек может решить, что это расход. И
    // возврату: он уменьшит траты в категории, за которую вернули деньги.
    const out = operations.map((op, n) => ({ op, n })).filter(({ op }) => op.direction === 'out' || op.refund === true);
    if (out.length === 0) return;
    setSorting(true);
    void categorizeBasket(
      out.map(({ op }) => (op.bankCategory ? `${op.title} (${op.bankCategory})` : op.title)),
      catalog,
    ).then((picked) => {
      setSorting(false);
      if (!picked) return;
      setRows((prev) => prev.map((r, n) => {
        const k = out.findIndex((s) => s.n === n);
        if (k < 0 || r.sure) return r;
        const id = picked[k];
        return id ? { ...r, categoryId: id, sure: true } : r;
      }));
      // Покупке не нашлось категории — модель предлагает новую. Переводы и
      // возвраты не в счёт: новая категория нужна тратам.
      void nc.suggest(out.flatMap(({ op, n }, k) => (picked[k] === null && spends(op) && !remembered.has(nameKey(op.title))
        ? [{ n, name: op.bankCategory ? `${op.title} (${op.bankCategory})` : op.title }] : [])));
    });
    // Категории подбираются один раз на снимок.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const duplicate = (r: Row): boolean => recordedAmounts(
    state.transactions, dayOf(r),
    r.mode === 'transfer' ? 'transfer' : r.op.direction === 'in' ? 'income' : 'expense',
  ).has(r.op.amount);
  // Уже записанное снимается с галочки — по дню, который известен сейчас.
  // Дальше решает человек: он мог сделать две одинаковые покупки.
  useEffect(() => {
    setRows((prev) => prev.map((r) => (r.on && duplicate(r) ? { ...r, on: false } : r)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const set = (n: number, patch: Partial<Row>) => setRows((prev) => prev.map((r, j) => (j === n ? { ...r, ...patch } : r)));
  const accountOf = (op: StatementOp) => labelMap[op.account ?? NO_LABEL] ?? '';
  const nameOf = (id: string) => accounts.find((a) => a.id === id)?.name;
  const catOf = (r: Row) => nc.all.find((c) => c.id === r.categoryId);
  const partOf = (r: Row): PlanPart => catOf(r)?.defaultPart ?? classifyItemPart(r.op.title);
  const streamOf = (r: Row) => state.incomeStreams.find((x) => x.isActive && x.id === r.streamId);
  const incomeCatOf = (r: Row) => incomeCats.find((c) => c.id === r.incomeCatId);

  /** Чего не хватает строке, чтобы её записать. */
  const missing = (r: Row): string | null => {
    if (!r.on) return null;
    const incoming = r.op.direction === 'in';
    if (r.mode === 'transfer') {
      // У входящего перевода счёт карты — это «куда», а выбрать нужно «откуда».
      if (!accountOf(r.op)) return incoming ? 'выбери, куда' : 'выбери, откуда';
      if (!r.pairId || r.pairId === accountOf(r.op)) return incoming ? 'выбери, откуда' : 'выбери, куда';
      return null;
    }
    if (!accountOf(r.op)) return 'выбери карту';
    if (r.mode === 'income' && r.refund) return catOf(r) ? null : 'выбери, за что вернули';
    if (r.mode === 'income') {
      if (!incomeCatOf(r)) return 'выбери категорию';
      if (partialRow(r) && r.waitRest === null) return 'ответь про остаток';
    }
    return null;
  };
  /*
    Части одной зарплаты в одной выписке. Каждая строка видит строки раньше
    по дате — их остаток ещё не записан. Банк показывает новые сверху,
    поэтому порядок на экране значения не имеет: считаем и записываем по
    датам. Иначе вторая часть считалась бы от всей зарплаты, а последним
    ответом об остатке стала бы старая строка.
  */
  const byTime = (a: { r: Row; i: number }, b: { r: Row; i: number }) =>
    dayOf(a.r).localeCompare(dayOf(b.r))
    || (a.r.time ?? a.r.op.time ?? '').localeCompare(b.r.time ?? b.r.op.time ?? '')
    || b.i - a.i;
  const pendingOf = new Map<number, Money | null>();
  const restOf = new Map<number, Money | undefined>();
  {
    const extra: PeriodIncome[] = [];
    rows.map((r, i) => ({ r, i }))
      .filter(({ r, i }) => (r.on || i === open) && r.mode === 'income' && !r.refund && !!streamOf(r))
      .sort(byTime)
      .forEach(({ r, i }, n) => {
        const st = streamOf(r)!;
        const amount = kop(r.op.amount);
        const p = pendingRest(state, st.id, dayOf(r), extra);
        const rest = restToRecord(st, amount, p, r.waitRest);
        pendingOf.set(i, p);
        restOf.set(i, rest);
        extra.push({
          txId: `row-${i}`, date: dayOf(r), amount, kind: 'regular', streamId: st.id,
          // Позже всего записанного: строка ещё не сохранена.
          recordedAt: `9999-${String(n).padStart(6, '0')}`,
          ...(rest !== undefined ? { expectedRest: rest } : {}),
        });
      });
  }
  const partialRow = (r: Row): boolean => {
    const p = pendingOf.get(rows.indexOf(r)) ?? null;
    const amount = kop(r.op.amount);
    return p !== null ? amount < p : partialIncome(streamOf(r), amount);
  };
  const chosen = rows.filter((r) => r.on);
  const gaps = chosen.filter((r) => missing(r) !== null).length;
  const total = chosen.reduce((a, r) => a + r.op.amount, 0);
  // Сумма прихода и расхода вместе ничего не значит — тогда только число.
  const oneWay = new Set(chosen.map((r) => r.op.direction)).size === 1;

  const save = async () => {
    if (busy || chosen.length === 0 || gaps > 0) return;
    setBusy(true); setErr(null);
    try {
      // По датам: части одной зарплаты записываются в том порядке, в каком пришли.
      const ordered = chosen.map((r) => ({ r, i: rows.indexOf(r) })).sort(byTime).map(({ r }) => r);
      // Новые категории появляются вместе с операциями, и только те, что достались записываемым.
      const real = await nc.materialize(ordered.filter(spendCat).map((r) => r.categoryId));
      // Одно наступление платежа — одной строкой: вторая «ЖКХ» в той же
      // выписке его второй раз не закрывает.
      const paid = new Set<string>();
      for (const r of ordered) {
        const time = r.time ?? r.op.time;
        const when = { date: dayOf(r), ...(time ? { time } : {}) };
        const amount = kop(r.op.amount);
        // Название из банка остаётся: по нему операцию узнают в списке.
        const note = r.comment.trim() ? `${r.op.title} — ${r.comment.trim()}` : r.op.title;
        if (r.mode === 'transfer') {
          const [fromId, toId] = r.op.direction === 'in' ? [r.pairId, accountOf(r.op)] : [accountOf(r.op), r.pairId];
          await addTransfer({ fromId, toId, amount, note, ...when });
        } else if (r.mode === 'income' && r.refund) {
          await addIncomeTx({
            accountId: accountOf(r.op), amount, categoryId: real(r.categoryId), part: partOf(r),
            incomeKind: 'refund', source: 'Возврат', note, ...when,
          });
        } else if (r.mode === 'income') {
          const stream = streamOf(r);
          await addIncomeTx({
            accountId: accountOf(r.op), amount, categoryId: r.incomeCatId,
            source: stream?.name ?? incomeCatOf(r)?.name ?? 'Поступление', note, ...when,
            // Связь — только с доходом, который назвал человек: как в ручной записи.
            ...(stream ? { incomeStreamId: stream.id } : {}),
            ...(() => {
              const rest = stream ? restOf.get(rows.indexOf(r)) : undefined;
              return rest !== undefined ? { expectedRest: rest } : {};
            })(),
          });
        } else {
          const categoryId = real(r.categoryId);
          const tx = await addExpense({
            accountId: accountOf(r.op), amount, categoryId, part: partOf(r),
            note, source: 'bank_screen', ...when,
          });
          const m = r.notPayment ? null : paymentFor(state, { date: dayOf(r), categoryId, note, amount });
          if (m && !paid.has(m.id)) {
            paid.add(m.id);
            await linkPaymentTx(tx.id, m.obligationId, m.scheduledDate);
          }
        }
      }
      // Форма записи откроется на виде последней записанной операции.
      const last = ordered[ordered.length - 1];
      if (last?.mode) rememberType(last.mode);
      // Сопоставление подписей запоминается только после записи: выбор,
      // сделанный и брошенный, ничего не обещал.
      const saved = await getMeta<LabelMap>(LABELS_KEY).catch(() => undefined);
      await setMeta(LABELS_KEY, { ...saved, ...Object.fromEntries(Object.entries(labelMap).filter(([, id]) => id)) });
      onDone(`Записано ${plural(chosen.length, 'операция', 'операции', 'операций')}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось записать');
      setBusy(false);
    }
  };

  /** Серая строка под названием: что будет записано и чего не хватает. */
  const subOf = (r: Row, n: number): { text: ReactNode; warn: boolean } => {
    if (r.mode === null) {
      return { text: r.op.direction === 'in' ? 'Поступление или перевод? Выбери' : 'Расход или перевод? Выбери', warn: true };
    }
    const lack = missing(r);
    // Уже записанное и снятое с галочки — одно слово, без того, чего не
    // хватает: записываться оно не будет, и лишнее только отвлекает.
    if (!r.on && duplicate(r)) return { text: 'Уже записано', warn: false };
    const again = duplicate(r) ? 'уже записано' : null;
    if (r.mode === 'transfer') {
      const [from, to] = r.op.direction === 'in' ? [r.pairId, accountOf(r.op)] : [accountOf(r.op), r.pairId];
      return { text: [`Перевод · ${nameOf(from) ?? 'откуда?'} → ${nameOf(to) ?? 'куда?'}`, again].filter(Boolean).join(' · '), warn: lack !== null };
    }
    if (r.mode === 'income' && r.refund) {
      const acc = nameOf(accountOf(r.op)) ?? (r.on ? 'выбери карту' : null);
      return { text: ['Возврат', catOf(r)?.name ?? 'выбери, за что', acc, again].filter(Boolean).join(' · '), warn: lack !== null };
    }
    if (r.mode === 'income') {
      const stream = streamOf(r);
      const acc = nameOf(accountOf(r.op)) ?? (r.on ? 'выбери карту' : null);
      const rest = lack === 'ответь про остаток' ? lack : null;
      return {
        text: [incomeCatOf(r)?.name ?? 'выбери категорию', acc, stream ? `доход «${stream.name}»` : null, rest, again].filter(Boolean).join(' · '),
        warn: lack !== null,
      };
    }
    const cat = catOf(r)?.name ?? 'Без категории';
    const acc = nameOf(accountOf(r.op)) ?? (r.on ? 'выбери карту' : null);
    // Предложена новая категория, а решения нет — операция пока в «Прочем».
    const unsure = nc.pending.has(n) ? 'выбери категорию' : !sorting && !nc.busy && !r.sure ? 'проверь категорию' : null;
    const rest = [acc, unsure, again].filter(Boolean).map((x) => ` · ${x}`).join('');
    return { text: isNewCategory(r.categoryId) ? <>{cat} <NewTag />{rest}</> : `${cat}${rest}`, warn: lack !== null };
  };

  const toggle = (n: number) => {
    const r = rows[n]!;
    // Нерешённую строку отметить нельзя: сначала человек говорит, что это.
    if (r.mode === null) { setOpen(n); return; }
    set(n, { on: !r.on });
  };

  const opened = open !== null ? rows[open] : undefined;

  return (
    <>
      <div className="imphead">
        <div>
          <b>{foundText(operations.length)}</b>
          <small>{operations.length === 1 ? 'Проверь её и запиши. Нажми на строку, чтобы поправить' : 'Отметь нужные. Нажми на строку, чтобы поправить'}</small>
        </div>
        {undated && (
          <label className="impday">
            {dayWord(date, today)} <Icon name="chevron-down" size={12} />
            <DateButton overlay label="День операций" value={date} max={today} onChange={(d) => setDate(d as PlainDate)} />
          </label>
        )}
      </div>
      {dropped > 0 && (
        <div className="note warn">
          {plural(dropped, 'строка не прочиталась', 'строки не прочитались', 'строк не прочиталось')} — её
          запиши вручную.
        </div>
      )}

      <NewCatsBlock nc={nc} ids={rows.filter((r) => r.on && spendCat(r)).map((r) => r.categoryId)}
        unit={['операция', 'операции', 'операций']} />
      <NewCatsAsk nc={nc} nameOf={(n) => rows[n]?.op.title ?? ''} on={(n) => !!rows[n]?.on && rows[n]?.mode === 'expense'} />
      <div className="blk">
        {rows.map((r, n) => {
          const sub = subOf(r, n);
          const cat = catOf(r);
          const shown = r.op.direction === 'in' && !r.refund ? incomeCatOf(r) : cat;
          const icon = r.mode === 'transfer' || r.mode === null
            ? <div className="tile"><Icon name="arrow-right-arrow-left" size={17} /></div>
            : shown ? <CategoryGlyph icon={shown.icon} color={colorOf(shown)} />
              : <div className="tile"><Icon name={r.op.direction === 'in' ? 'arrow-down-left' : 'circle-question'} size={17} /></div>;
          return (
            <ImportRow key={`${r.op.title}-${n}`}
              icon={icon} title={r.op.title} sub={sub.text} warn={sub.warn}
              amount={`${r.op.direction === 'in' ? '+' : '−'}${formatMoney(kop(r.op.amount))}`}
              muted={!r.on}
              check={{ on: r.on, label: `Записать «${r.op.title}»`, onToggle: () => toggle(n) }}
              onOpen={() => setOpen(n)} openLabel={`Подробности «${r.op.title}»`} />
          );
        })}
      </div>
      {(sorting || nc.busy) && <div className="note">Подбираю категории…</div>}
      {err && <div className="note warn">{err}</div>}
      <NewModeQuestion nc={nc} names={[...nc.pending.keys()].map((n) => rows[n]?.op.title ?? '')} />

      <ImportBar hint={gaps > 0 ? `Не хватает данных у ${plural(gaps, 'операции', 'операций', 'операций')} — они отмечены жёлтым` : null}>
        <button className="btn pri" disabled={busy || chosen.length === 0 || gaps > 0} onClick={() => void save()}>
          {busy ? 'Записываю…' : chosen.length === 0 ? 'Отметь, что записать'
            : `Записать ${plural(chosen.length, 'операцию', 'операции', 'операций')}${oneWay ? ` · ${formatMoney(kop(total))}` : ''}`}
        </button>
        <button className="btn ghost" onClick={onCancel}>Не записывать</button>
      </ImportBar>

      {opened && open !== null && (
        <OperationSheet state={state} row={opened} accounts={accounts} categories={nc.all} onCategories={setCategories}
          account={accountOf(opened.op)}
          onAccount={(id) => setLabelMap((m) => ({ ...m, [opened.op.account ?? NO_LABEL]: id }))}
          day={dayOf(opened)} shotDay={opened.op.date ?? date} today={today}
          pending={pendingOf.get(open) ?? null}
          // Правка в листе — это намерение записать: поступление без галочки,
          // настроенное и брошенное неотмеченным, человек счёл бы записанным.
          // Уже записанное не отмечается само — повтор решает человек.
          onChange={(patch) => {
            if ('categoryId' in patch) nc.resolve(open);
            set(open, 'on' in patch || duplicate(opened) ? patch : { ...patch, on: true });
          }}
          onClose={() => setOpen(null)} />
      )}
    </>
  );
}

/**
 * Лист одной строки: что это и куда записать — по макету «Корректировка
 * операции» (Figma 23:880, 63:625).
 *
 * Поля те же, что у ручной записи, — категория, счёт, «откуда» и «куда»,
 * ожидаемый приход, дата, заметка, — чтобы человек не учил второй способ
 * записать то же самое. Не записывать строку — снять с неё галочку в списке.
 */
function OperationSheet({ state, row, accounts, categories, onCategories, account, onAccount, day, shotDay, today, pending, onChange, onClose }: {
  state: AppState;
  row: Row;
  accounts: Account[];
  categories: AppState['categories'];
  onCategories: (c: AppState['categories']) => void;
  /** Счёт, стоящий за подписью банка. */
  account: string;
  onAccount: (id: string) => void;
  /** День, которым операция запишется. */
  day: PlainDate;
  /** День, который показал банк. */
  shotDay: PlainDate;
  today: PlainDate;
  /** Сколько ещё ждут по сроку с учётом строк выше по дате. */
  pending: Money | null;
  onChange: (patch: Partial<Row>) => void;
  onClose: () => void;
}) {
  const mode = row.mode;
  const incoming = row.op.direction === 'in';
  const pick = (m: Mode) => onChange({ mode: m, on: true });
  const label = row.op.account;
  const amount = kop(row.op.amount);
  const stream = state.incomeStreams.find((x) => x.isActive && x.id === row.streamId);
  const partial = pending !== null ? amount < pending : partialIncome(stream, amount);
  const refund = mode === 'income' && row.refund;
  const unanswered = mode === 'income' && !refund && partial && row.waitRest === null;
  const time = row.time ?? row.op.time;
  const when = <CardWhen text={`${dayWord(day, today)}${time ? `, ${time}` : ''}`} date={day} time={time ?? '12:00'}
    max={`${today}T23:59`} onChange={(d, t) => onChange({ date: d as PlainDate, time: t })} />;
  // Карта со снимка — та сторона перевода, где видна операция.
  const card = <CardAccount label={incoming ? 'Куда' : 'Откуда'}
    pickerTitle={incoming ? 'Перевести на счёт' : 'Перевести со счёта'}
    accounts={accounts} value={account} onChange={onAccount} />;
  const pair = <CardAccount label={incoming ? 'Откуда' : 'Куда'}
    pickerTitle={incoming ? 'Перевести со счёта' : 'Перевести на счёт'}
    accounts={accounts.filter((a) => a.id !== account)} value={row.pairId}
    onChange={(id) => onChange({ pairId: id })} />;
  // Пояснение листа — у заголовка. Пока строка не решена — как отличить
  // перевод, потом — что будет с переводом и подписью карты.
  const hint = mode === null
    ? (incoming
      ? 'Если деньги пришли с твоего же счёта — это перевод. Зарплата, возврат, деньги от другого человека — поступление.'
      : 'Перевод на карту другого банка или другому человеку бывает расходом — например, оплата. Если деньги ушли на твой же счёт, это перевод.')
    : [
      mode === 'transfer' ? `Перевод между своими счетами ${incoming ? 'доходом' : 'расходом'} не считается.` : null,
      label ? `Счёт для «${label}» запомню и подставлю в такие же операции.` : null,
    ].filter(Boolean).join(' ');
  return (
    <SheetWrap title={row.op.title} onClose={onClose} className="rcpt" tip={hint}>
      <div className="segs">
        {incoming
          ? <button className={`seg${mode === 'income' ? ' on' : ''}`} onClick={() => pick('income')}>Поступление</button>
          : <button className={`seg${mode === 'expense' ? ' on' : ''}`} onClick={() => pick('expense')}>Расход</button>}
        <button className={`seg${mode === 'transfer' ? ' on' : ''}`} onClick={() => pick('transfer')}>Перевод</button>
      </div>
      <div className="rchead">
        <div className="rcamount num">{incoming ? '+' : '−'}{formatMoney(amount)}</div>
        <small>
          {[dayWord(shotDay, today), row.op.time, row.op.bankCategory, label ? `карта «${label}»` : null]
            .filter(Boolean).join(' · ')}
        </small>
      </div>

      {mode === 'expense' && (
        <div className="rccard rcsel">
          <CardAccount label="Счёт" pickerTitle="Списать со счёта" accounts={accounts} value={account} onChange={onAccount} />
          <CardCategory categories={categories} kind="expense" value={row.categoryId} unsure={!row.sure}
            onUpdated={onCategories} onChange={(id) => onChange({ categoryId: id, sure: true })} />
          {when}
        </div>
      )}
      {mode === 'expense' && (() => {
        const m = paymentFor(state, { date: day, categoryId: row.categoryId, note: `${row.op.title} ${row.comment}`, amount: kop(row.op.amount) });
        return m && (
          <label className="oprow checkrow">
            <span>Засчитать как оплату «{m.label}» за {dayMonth(m.scheduledDate, today)}</span>
            <input type="checkbox" checked={!row.notPayment} onChange={(e) => onChange({ notPayment: !e.target.checked })} />
          </label>
        );
      })()}
      {mode === 'income' && (
        <>
          <div className="rccard rcsel">
            <CardAccount label="Счёт" pickerTitle="Зачислить на счёт" accounts={accounts} value={account} onChange={onAccount} />
            {refund
              ? <CardCategory categories={categories} kind="expense" value={row.categoryId} label="За что вернули"
                onUpdated={onCategories} onChange={(id) => onChange({ categoryId: id, sure: true })} />
              : <CardCategory categories={categories} kind="income" value={row.incomeCatId}
                onUpdated={onCategories} onChange={(id) => onChange({ incomeCatId: id })} />}
            {!refund && <CardPick label="Ожидаемый приход" value={row.streamId} options={incomeStreamOptions(state)}
              onChange={(id) => onChange({ streamId: id, waitRest: null })} />}
            {when}
          </div>
          <label className="oprow checkrow">
            <span>Это возврат за покупку</span>
            <input type="checkbox" checked={row.refund}
              onChange={(e) => onChange({ refund: e.target.checked, streamId: '', waitRest: null })} />
          </label>
          {!refund && <IncomeLink stream={stream} value={amount} waitRest={row.waitRest} pending={pending}
            onWaitRest={(wait) => onChange({ waitRest: wait })} />}
        </>
      )}
      {mode === 'transfer' && (
        <div className="rccard rcsel">{incoming ? <>{pair}{card}</> : <>{card}{pair}</>}{when}</div>
      )}
      {mode !== null && <NoteField value={row.comment} onChange={(comment) => onChange({ comment })} />}
      <div className="rcbar">
        <button className="rccta" disabled={mode === null || unanswered} onClick={onClose}>
          {unanswered ? 'Ответь про остаток' : 'Готово'}
        </button>
      </div>
    </SheetWrap>
  );
}
